/**
 * Sooner Form Validation
 * Handles validation for the "Need it sooner?" popup form
 */

class SoonerFormValidator {
  constructor() {
    this.form = document.getElementById('sooner-form');
    this.fields = {
      name: document.getElementById('sooner-name'),
      email: document.getElementById('sooner-email'),
      date: document.getElementById('sooner-date'),
      message: document.getElementById('sooner-message')
    };
    
    this.init();
  }

  init() {
    if (!this.form) return;
    
    this.setupEventListeners();
    this.createErrorMessages();
    this.checkForSuccessMessage();
  }

  checkForSuccessMessage() {
    // Check if we were redirected back with a success parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('contact_success') === 'true') {
      this.showSuccessMessage();
      // Clean up the URL
      const newUrl = window.location.href.split('?')[0];
      window.history.replaceState({}, document.title, newUrl);
      
      // Close popup after showing success
      setTimeout(() => {
        this.closePopup();
      }, 3000);
    }
  }

  setupEventListeners() {
    // Real-time validation on input
    Object.keys(this.fields).forEach(fieldName => {
      const field = this.fields[fieldName];
      if (field) {
        field.addEventListener('blur', () => this.validateField(fieldName));
        field.addEventListener('input', () => this.clearFieldError(fieldName));
      }
    });

    // Form submission validation
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  createErrorMessages() {
    Object.keys(this.fields).forEach(fieldName => {
      const field = this.fields[fieldName];
      if (field) {
        const fieldWrapper = field.closest('.field') || field.closest('.form-field');
        if (fieldWrapper && !fieldWrapper.querySelector('.error-message')) {
          const errorDiv = document.createElement('div');
          errorDiv.className = 'error-message';
          errorDiv.setAttribute('id', `${fieldName}-error`);
          fieldWrapper.appendChild(errorDiv);
        }
      }
    });
  }

  validateField(fieldName) {
    const field = this.fields[fieldName];
    const fieldWrapper = field.closest('.field') || field.closest('.form-field');
    const errorDiv = fieldWrapper.querySelector('.error-message');
    
    let isValid = true;
    let errorMessage = '';

    switch (fieldName) {
      case 'name':
        if (!field.value.trim()) {
          isValid = false;
          errorMessage = 'Please enter your name.';
        } else if (field.value.trim().length < 2) {
          isValid = false;
          errorMessage = 'Name must be at least 2 characters long.';
        }
        break;

      case 'email':
        if (!field.value.trim()) {
          isValid = false;
          errorMessage = 'Please enter a valid email address.';
        } else if (!this.isValidEmail(field.value.trim())) {
          isValid = false;
          errorMessage = 'Please enter a valid email address.';
        }
        break;

      case 'date':
        if (!field.value) {
          isValid = false;
          errorMessage = 'Please enter a valid date.';
        } else if (!this.isValidDate(field.value)) {
          isValid = false;
          errorMessage = 'Please enter a valid date.';
        } else if (this.isPastDate(field.value)) {
          isValid = false;
          errorMessage = 'Please select a future date.';
        }
        break;

      case 'message':
        // Sanitize the message value
        if (field.value) {
          field.value = this.sanitizeInput(field.value);
        }
        // Message is optional, so always valid
        isValid = true;
        break;
    }

    this.updateFieldStatus(fieldWrapper, errorDiv, isValid, errorMessage);
    return isValid;
  }

  validateAllFields() {
    let allValid = true;
    
    Object.keys(this.fields).forEach(fieldName => {
      if (!this.validateField(fieldName)) {
        allValid = false;
      }
    });
    
    return allValid;
  }

  updateFieldStatus(fieldWrapper, errorDiv, isValid, errorMessage) {
    if (isValid) {
      fieldWrapper.classList.remove('error');
      fieldWrapper.classList.add('success');
      if (errorDiv) {
        errorDiv.textContent = '';
      }
    } else {
      fieldWrapper.classList.remove('success');
      fieldWrapper.classList.add('error');
      if (errorDiv) {
        errorDiv.textContent = errorMessage;
      }
    }
  }

  clearFieldError(fieldName) {
    const field = this.fields[fieldName];
    const fieldWrapper = field.closest('.field') || field.closest('.form-field');
    
    if (fieldWrapper.classList.contains('error')) {
      fieldWrapper.classList.remove('error');
      const errorDiv = fieldWrapper.querySelector('.error-message');
      if (errorDiv) {
        errorDiv.textContent = '';
      }
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    
    if (!this.validateAllFields()) {
      // Focus on first error field
      const firstErrorField = this.form.querySelector('.field.error input, .form-field.error input, .field.error textarea, .form-field.error textarea');
      if (firstErrorField) {
        firstErrorField.focus();
      }
      return false;
    }

    // If validation passes, submit the form
    this.submitForm();
  }

  submitForm() {
    const submitButton = this.form.querySelector('.submit-button');
    const originalText = submitButton.textContent;
    
    // Disable button and show loading state
    submitButton.disabled = true;
    submitButton.textContent = 'Sending...';
    
    // For Shopify, we need to submit the form traditionally, not via fetch
    // But we'll try fetch first, and fall back to traditional submission
    
    // Create FormData object
    const formData = new FormData(this.form);
    
    // Try fetch first (some Shopify themes support this)
    fetch(this.form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (response.ok || response.status === 200) {
        return response.text();
      } else if (response.status === 400 || response.status === 422) {
        // Shopify often returns 400/422 for contact forms even when successful
        // Fall back to traditional form submission
        throw new Error('Fallback to traditional submission');
      } else {
        throw new Error('Network response was not ok');
      }
    })
    .then(data => {
      // Success via fetch
      this.showSuccessMessage();
      this.form.reset();
      setTimeout(() => {
        this.closePopup();
      }, 2000);
      
      // Re-enable button
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    })
    .catch(error => {
      console.log('Fetch failed, falling back to traditional form submission:', error);
      
      // Fall back to traditional form submission
      // Re-enable button first
      submitButton.disabled = false;
      submitButton.textContent = originalText;
      
      // Add a hidden field to indicate success redirect
      const successField = document.createElement('input');
      successField.type = 'hidden';
      successField.name = 'return_to';
      successField.value = window.location.href + '?contact_success=true';
      this.form.appendChild(successField);
      
      // Submit the form traditionally
      this.form.submit();
    });
  }

  showSuccessMessage() {
    const successDiv = document.createElement('div');
    successDiv.className = 'alert alert-success';
    successDiv.style.cssText = 'background: #d4edda; color: #155724; padding: 1rem; border-radius: 4px; margin-bottom: 1rem; border: 1px solid #c3e6cb;';
    successDiv.textContent = 'Thank you! Your message has been sent successfully.';
    
    this.form.insertBefore(successDiv, this.form.firstChild);
    
    // Remove success message after 5 seconds
    setTimeout(() => {
      if (successDiv.parentNode) {
        successDiv.parentNode.removeChild(successDiv);
      }
    }, 5000);
  }

  showErrorMessage(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.style.cssText = 'background: #f8d7da; color: #721c24; padding: 1rem; border-radius: 4px; margin-bottom: 1rem; border: 1px solid #f5c6cb;';
    errorDiv.textContent = message;
    
    this.form.insertBefore(errorDiv, this.form.firstChild);
    
    // Remove error message after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv);
      }
    }, 5000);
  }

  closePopup() {
    const popup = document.getElementById('sooner-popup');
    if (popup) {
      popup.style.display = 'none';
    }
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidDate(dateString) {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  }

  isPastDate(dateString) {
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to beginning of day
    return selectedDate < today;
  }

  sanitizeInput(input) {
    // Remove potentially harmful characters and scripts
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocols
      .replace(/on\w+\s*=/gi, '') // Remove event handlers
      .trim();
  }
}

// Initialize the validator when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  new SoonerFormValidator();
});

// Also initialize if called after DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    new SoonerFormValidator();
  });
} else {
  new SoonerFormValidator();
}